import {
  EXTRA_SMALL_CHIP_SX,
  FaroAiOutlinedChip,
} from "@/components/common/ai-assets/ai-chip";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { formatSparseCoordinateString } from "@/components/ui/scene-context-menu";
import {
  AiDescriptionReview,
  useAiDescriptionReviewStore,
} from "@/hooks/use-capture-ai-description";
import { useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  blue,
  FaroButton,
  FaroChip,
  FaroText,
  FaroToggleGroup,
  grey,
  NO_TRANSLATE_CLASS,
  ToggleItem,
  WorldCoordinateIcon,
} from "@faro-lotv/flat-ui";
import { GUID, isLoading, MaybeLoading } from "@faro-lotv/foundation";
import { alpha, Collapse, Fade, Skeleton } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useState, type JSX } from "react";
import { Vector3Tuple } from "three";
import {
  CoordinateMode,
  MOCK_PROJECT_CRS,
  toRealWorldPosition,
  useCoordinateModeStore,
} from "./capture-coordinates";

/** Style of a section of the capture preview, separated from the one above by a thin line */
export const PREVIEW_SECTION_SX = {
  borderTop: `1px solid ${grey[800]}`,
  pt: 1,
  mt: 1,
};

/** Height of the description, 6 lines of text, before it scrolls */
const DESCRIPTION_MAX_HEIGHT = 96;

/** Opacity of the scrollbar of the description */
const SCROLLBAR_OPACITY = 0.3;

/** Opacity of the background of the hovered and selected coordinate modes */
const TOGGLE_HOVER_OPACITY = 0.12;
const TOGGLE_SELECTED_OPACITY = 0.2;

/** Height of the fade at the edge of the description that has more text */
const DESCRIPTION_FADE = 16;

/** Opacity of the hover and pressed tints of the neutral ghost button, dark scheme */
const NEUTRAL_HOVER_OPACITY = 0.08;
const NEUTRAL_PRESSED_OPACITY = 0.16;

/**
 * The design's neutral buttons, dark scheme. Stand-ins for flat-ui's `variant="neutralGhost"` and
 * `variant="neutral"` of the dark-mode release, whose tokens they copy
 */
const NEUTRAL_GHOST_BUTTON_SX = {
  color: grey[100],
  backgroundColor: "transparent",
  "&:hover": { backgroundColor: alpha(grey[100], NEUTRAL_HOVER_OPACITY) },
  "&:active": { backgroundColor: alpha(grey[100], NEUTRAL_PRESSED_OPACITY) },
};
const NEUTRAL_BUTTON_SX = {
  color: grey[100],
  backgroundColor: grey[800],
  border: "none",
  "&:hover": { backgroundColor: grey[700], border: "none" },
  "&:active": { backgroundColor: grey[600] },
};

/**
 * @returns the description the AI wrote for the capture image, with a label saying whether it was
 * AI generated or verified by a user. Users who can edit the project can verify it on request
 */
export function AiDescriptionSection({
  description,
  imageId,
}: {
  /** The description, LOADING while fetched, undefined if the image has none */
  description: MaybeLoading<string>;
  /** The image the description was written for */
  imageId?: GUID;
}): JSX.Element | null {
  const [fade, setFade] = useState({ top: false, bottom: false });
  const [isVerifying, setIsVerifying] = useState(false);
  // The review done while this preview is open, which can still be undone
  const [justReviewed, setJustReviewed] = useState<AiDescriptionReview>();
  const canVerify = useAppSelector(selectHasWritePermission);
  const review = useAiDescriptionReviewStore((s) =>
    imageId ? s.reviews[imageId] : undefined,
  );
  const setReview = useAiDescriptionReviewStore((s) => s.setReview);

  if (!description) return null;

  if (isLoading(description)) {
    return (
      <Box component="div" sx={{ mt: 1 }}>
        <Skeleton height={12} sx={{ background: grey[800] }} />
        <Skeleton height={12} width="70%" sx={{ background: grey[800] }} />
      </Box>
    );
  }

  // Rejected before this preview opened: nothing to show
  if (review === "rejected" && !justReviewed) return null;

  function finishReview(result?: AiDescriptionReview): void {
    if (!imageId) return;
    setReview(imageId, result);
    setJustReviewed(result);
    setIsVerifying(false);
  }
  function updateFade(el: HTMLElement | null): void {
    if (!el) return;
    const next = {
      top: el.scrollTop > 1,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - 1,
    };
    if (next.top !== fade.top || next.bottom !== fade.bottom) setFade(next);
  }
  const top = fade.top ? DESCRIPTION_FADE : 0;
  const bottom = fade.bottom ? DESCRIPTION_FADE : 0;
  const mask = `linear-gradient(to bottom, transparent 0, #000 ${top}px, #000 calc(100% - ${bottom}px), transparent 100%)`;

  const isVerified = review === "verified";
  const isRejected = review === "rejected";
  const undo = (
    <FaroButton
      variant="ghost"
      size="xs"
      dark
      sx={NEUTRAL_GHOST_BUTTON_SX}
      onClick={() => finishReview()}
    >
      Undo
    </FaroButton>
  );

  return (
    <>
      {/* Both states stay mounted so the preview shrinks and grows smoothly on reject and undo */}
      <Collapse in={!isRejected}>
        <Stack sx={{ mt: 1, gap: 1 }}>
          <Box
            component="div"
            ref={updateFade}
            onScroll={(ev) => updateFade(ev.currentTarget)}
            // Scrolling the description must not zoom or scroll the view behind the preview
            onWheel={(ev) => ev.stopPropagation()}
            tabIndex={fade.top || fade.bottom ? 0 : undefined}
            aria-label={
              isVerified ? "Verified description" : "AI-generated description"
            }
            sx={{
              maxHeight: DESCRIPTION_MAX_HEIGHT,
              overflowY: "auto",
              overscrollBehavior: "contain",
              scrollbarWidth: "thin",
              scrollbarColor: `${alpha(grey[100], SCROLLBAR_OPACITY)} transparent`,
              mr: -1,
              pr: 1,
              maskImage: mask,
            }}
          >
            <FaroText variant="bodyS" component="p" color={grey[100]}>
              {description}
            </FaroText>
          </Box>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: 24,
            }}
          >
            {isVerifying ? (
              <Fade in>
                <FaroText variant="bodyS" color={grey[300]}>
                  Is this description correct?
                </FaroText>
              </Fade>
            ) : (
              <Fade in>
                <Box component="span" sx={{ display: "flex" }}>
                  {isVerified ? (
                    <FaroChip
                      label="Verified description"
                      variant="outlined"
                      size="small"
                      textTransform="none"
                      tooltip="A user has checked and confirmed this description"
                      color={grey[100]}
                      borderColor={blue[400]}
                      backgroundColor="transparent"
                      sx={EXTRA_SMALL_CHIP_SX}
                    />
                  ) : (
                    <FaroAiOutlinedChip
                      label="AI-generated description"
                      tooltip="This description was written automatically by AI"
                    />
                  )}
                </Box>
              </Fade>
            )}
            {isVerified && justReviewed === "verified" && undo}
            {!isVerified && canVerify && !!imageId && (
              <FaroButton
                variant="ghost"
                size="xs"
                dark
                sx={NEUTRAL_GHOST_BUTTON_SX}
                onClick={() => setIsVerifying((verifying) => !verifying)}
              >
                {isVerifying ? "Cancel" : "Verify"}
              </FaroButton>
            )}
          </Stack>
          {/* Verifying starts only when the user asks for it, and slides in below the label */}
          <Collapse in={isVerifying && !isVerified}>
            <Stack direction="row" sx={{ gap: 1, justifyContent: "flex-end" }}>
              <FaroButton
                variant="secondary"
                size="s"
                dark
                sx={NEUTRAL_BUTTON_SX}
                onClick={() => finishReview("rejected")}
              >
                Reject
              </FaroButton>
              <FaroButton
                variant="primary"
                size="s"
                dark
                onClick={() => finishReview("verified")}
              >
                Accept
              </FaroButton>
            </Stack>
          </Collapse>
        </Stack>
      </Collapse>
      <Collapse in={isRejected}>
        <Stack direction="row" sx={{ mt: 1, alignItems: "center", gap: 1 }}>
          <FaroText variant="bodyS" color={grey[400]}>
            Description rejected
          </FaroText>
          {undo}
        </Stack>
      </Collapse>
    </>
  );
}

const COORDINATE_MODES: Array<ToggleItem<CoordinateMode>> = [
  { value: "project", label: "Project" },
  { value: "realWorld", label: "Real-world" },
];

/** @returns the position of the capture, switchable between project and real-world coordinates */
export function CoordinatesSection({
  coordinates,
}: {
  /** The position of the capture in the project (y-up) */
  coordinates: Vector3Tuple;
}): JSX.Element {
  const { mode, setMode } = useCoordinateModeStore();
  const distanceUnit = useDistanceUnit();
  const isRealWorld = mode === "realWorld";

  const formatted = isRealWorld
    ? formatSparseCoordinateString(
        toRealWorldPosition(coordinates),
        distanceUnit,
        ["E", "N", "H"],
      )
    : formatSparseCoordinateString(coordinates, distanceUnit);

  return (
    <Stack sx={{ ...PREVIEW_SECTION_SX, gap: 0.75 }}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <FaroText variant="labelM" color={grey[100]}>
          Coordinates
        </FaroText>
        <FaroToggleGroup
          items={COORDINATE_MODES}
          value={mode}
          onChange={setMode}
          sxGroup={{
            p: "2px",
            gap: 0.25,
            backgroundColor: grey[900],
            border: `1px solid ${grey[800]}`,
          }}
          sxButtons={{
            px: 0.75,
            py: 0.25,
            backgroundColor: "transparent",
            "& .MuiTypography-root": { fontSize: "12px", color: grey[300] },
            "&:hover:not(.Mui-selected)": {
              backgroundColor: alpha(grey[500], TOGGLE_HOVER_OPACITY),
            },
            "&.Mui-selected, &.Mui-selected:hover": {
              backgroundColor: alpha(blue[400], TOGGLE_SELECTED_OPACITY),
              boxShadow: "none",
            },
            "&.Mui-selected .MuiTypography-root, &:hover .MuiTypography-root": {
              color: blue[300],
            },
          }}
        />
      </Stack>
      <FaroText
        className={NO_TRANSLATE_CLASS}
        variant="bodyS"
        color={grey[100]}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {formatted}
      </FaroText>
      {isRealWorld && (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
          <WorldCoordinateIcon sx={{ fontSize: "16px", color: grey[300] }} />
          <FaroText variant="bodyS" color={grey[300]}>
            {MOCK_PROJECT_CRS.name} · {MOCK_PROJECT_CRS.heightType}
          </FaroText>
        </Stack>
      )}
    </Stack>
  );
}
